import { useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { Input } from '@antimatterhq/ui/components/input';
import { Label } from '@antimatterhq/ui/components/label';
import { useOnKeyDown } from '@/utils/util';
import { FormMessage } from '@/components/form/form';
import { cn } from '@/lib/utils';

export type DetectionCategoryDialogProps = {
  onSave: () => void;
  category?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setCategory: (category: string) => void;
  isEdit: boolean;
  existingCategories: string[];
};

export const DetectionCategoryDialog = ({
  onSave,
  category,
  open,
  onOpenChange,
  setCategory,
  isEdit,
  existingCategories,
}: DetectionCategoryDialogProps) => {
  const [error, setError] = useState<string | undefined>(undefined);

  const { handleKeyDown } = useOnKeyDown({
    enabled: !!category,
    onEnter: onSave,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCategory(value);

    const isDuplicate = existingCategories.some(
      (cat) => cat.toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate) {
      setError(`Category "${value}" already exists`);
      return;
    }

    setError(undefined);
  };

  const handleOpenChange = (openState: boolean) => {
    setError(undefined);
    onOpenChange(openState);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit && 'Edit detection category'}
            {!isEdit && 'Add detection category'}
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label htmlFor="category" id="category-label">
            Detection category*
          </Label>
          <Input
            id="category"
            name="category"
            value={category}
            onChange={handleChange}
            autoComplete="off"
            onKeyDown={handleKeyDown}
            aria-labelledby="category-label"
            data-testid="detection-category-input"
          />
          {error && (
            <FormMessage
              type="error"
              className={cn('mt-2')}
              data-testid="form-error-detection-category"
            >
              {error}
            </FormMessage>
          )}
        </div>
        <DialogFooter>
          <Button
            onClick={onSave}
            variant="primary"
            disabled={!category || !!error}
            data-testid="save-detection-category-button"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
